import { IDecodedToken } from "@core/interfaces/decoded-token.interface";
import { ILogin } from "@core/interfaces/login.interface";
import { AuthRequest } from "@core/requests/auth.request";
import { UserRequest } from "@core/requests/user.request";
import { AnalyticsService } from "@core/services/analytics.service";
import { ToastService } from "@core/services/toast.service";
import { TUserFullInformation } from "@core/types/user-full-information.type";

import { Observable, of, Subject, switchMap, take, tap } from "rxjs";

import { Nullable } from "primeng/ts-helpers";

import { Injectable, signal, WritableSignal } from "@angular/core";
import { Router } from "@angular/router";

import { TenantService } from "./tenant.service";

@Injectable({
  providedIn: "platform",
})
export class AuthenticationService {
  private decodedTokenSignal: WritableSignal<IDecodedToken | null> =
    signal(null);
  private credential: TUserFullInformation | null = null;
  private credentialChange = new Subject<TUserFullInformation | null>();

  constructor(
    private toastService: ToastService,
    private authRequest: AuthRequest,
    private router: Router,
    private tenantService: TenantService,
    private userService: UserRequest,
    private analyticsService: AnalyticsService,
  ) {
    this.bootstrapCredentials();
  }

  private doUserLogin(credential: TUserFullInformation): void {
    this.credential = credential;
    this.updateCredentialState(credential);
    localStorage.setItem("authorization", JSON.stringify(credential));

    this.analyticsService.setUser(credential);

    if (credential.firstAccess) {
      this.router
        .navigate(["/doctor-first-access"])
        .catch(() => new Error("Failed to redirect"));
      return;
    }

    const defaultInstitution = credential.tenants?.find(
      (tenant) => tenant.defaultInstitution,
    );

    if (!defaultInstitution) {
      console.error("Default institution not found");
      this.router
        .navigate(["/app"])
        .catch(() => new Error("Failed to redirect to auth"));
      return;
    }

    this.tenantService.setTenant(defaultInstitution);

    this.router.navigate(["/app"]).catch(console.error);
  }

  updateCredentialState(credential: TUserFullInformation | null): void {
    if (!credential) {
      this.credential = null;
      localStorage.removeItem("authorization");

      this.credentialChange.next(this.credential);

      return;
    }

    this.credential = credential;

    this.decodedTokenSignal.update(() =>
      this.decodeJwt(credential.accessToken),
    );

    localStorage.setItem("authorization", JSON.stringify(credential));

    this.credentialChange.next(this.credential);
  }

  private decodeJwt(jwt: string): IDecodedToken {
    return JSON.parse(atob(jwt.split(".")[1])) as IDecodedToken;
  }

  getAuthorization(): TUserFullInformation | null {
    const authorization = localStorage.getItem("authorization");

    return authorization
      ? (JSON.parse(authorization) as TUserFullInformation)
      : null;
  }

  private bootstrapCredentials(): void {
    const authorization = localStorage.getItem("authorization");

    if (authorization) {
      const credential = JSON.parse(authorization) as TUserFullInformation;

      return this.updateCredentialState(credential);
    }

    return this.updateCredentialState(null);
  }

  getUserFullInformation(credential: ILogin): Observable<TUserFullInformation> {
    return this.userService.getUserInformation(credential.accessToken).pipe(
      take(1),
      switchMap((fullInformation) => {
        return of({
          ...fullInformation,
          accessToken: credential.accessToken,
          refreshToken: credential.refreshToken,
          expiresIn: credential.expiresIn,
        } as TUserFullInformation);
      }),
    );
  }

  loginWithCustomFunction$(
    fn: () => Observable<ILogin>,
    sso = false,
  ): Observable<TUserFullInformation | null> {
    return fn().pipe(
      take(1),
      switchMap((credential) => {
        if (!credential.password && !sso) {
          this.toastService.error(
            "Erro",
            // eslint-disable-next-line max-len
            "Você ainda não configurou uma senha. Clique em “Esqueci minha senha” para configurar agora e acessar a Ymunity.",
          );

          return of(null);
        }

        return this.getUserFullInformation(credential);
      }),
      tap((credential) => {
        if (!credential) return;

        if (!credential?.tenants.length) {
          throw new Error("Not tenant found");
        }

        this.tenantService.setAvailableTenants(credential.tenants);

        const defaultTenant = credential.tenants.find(
          (tenant) => tenant.defaultInstitution,
        );

        this.tenantService.setTenant(defaultTenant ?? credential.tenants[0]);

        this.doUserLogin(credential);
      }),
    );
  }

  authenticate$(
    username: Nullable<string>,
    password: Nullable<string>,
    token: string,
  ): Observable<TUserFullInformation | null> {
    return this.loginWithCustomFunction$(() =>
      this.authRequest.login(username, password, token),
    );
  }

  logoff(redirect = true): void {
    this.updateCredentialState(null);

    this.analyticsService.clearUser();

    localStorage.clear();
    this.tenantService.setTenant(null);
    this.tenantService.setAvailableTenants(null);

    if (redirect) window.location.href = "/login";
  }

  setCredential(credential: ILogin | TUserFullInformation): void {
    if (!this.credential) {
      throw new Error("Credential not found");
    }

    this.updateCredentialState({
      ...this.credential,
      accessToken: credential.accessToken,
      refreshToken: credential.refreshToken,
      expiresIn: credential.expiresIn,
    });
  }

  getCredential(): TUserFullInformation | null {
    return this.credential;
  }

  getDecodedToken(): IDecodedToken | null {
    return this.decodedTokenSignal();
  }

  getCredentialChanges(): Observable<TUserFullInformation | null> {
    return this.credentialChange.asObservable();
  }
}
